import { useEffect, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useMailchimp } from '../../user/use-mailchimp'
import useStorage from '../../use-storage'
import EmailOfferPic from '@/images/illustrations/signpostToBetterHealth.png'
import * as UI from '@/ui'

type SignupFormValue = {
  email: string
}

export const EmailOfferBanner = () => {
  const { getItem, setItem } = useStorage()
  const [showModal, setShowModal] = useState(false)
  const { signUp, success, isReady } = useMailchimp()
  const handleSubmit = async (value: SignupFormValue) => {
    const res = await signUp({
      email: value.email,
      source: 'welcomeEmailOffer_popUp',
      tags: ['WELCOMESERIES', 'newsletter'],
    })
    // send event to trigger mc browsing tagging on /tests/[slug]
    if (typeof window !== 'undefined' && res?.success) {
      window.dispatchEvent(new CustomEvent('newsletterSignup', { detail: value.email }))
    }
  }

  // show pop up 30 sec after mounted
  useEffect(() => {
    const timer = setTimeout(() => {
      // don't show modal if it has already been shown
      const modalHasShown = getItem('welcomeEmailOffer', 'session')
      if (!modalHasShown) {
        setShowModal(true)
        setItem('welcomeEmailOffer', true, 'session')
        TagManager.dataLayer({
          dataLayer: {
            event: 'showWelcomeEmailOffer',
          },
        })
      }
    }, 30000)
    // clear timer if the component unmounts
    return () => clearTimeout(timer)
  }, [])

  return (
    <>
      <UI.Modal color="none" container={false} show={showModal} onClose={() => setShowModal(false)} disableOutsideClose>
        <div className="max-w-lg rounded-xl bg-selphGreen-500 pt-4 sm:w-lg md:mx-auto">
          <UI.Image src={EmailOfferPic} alt="Email Offer" width="175" placeholder="blur" className="mx-auto" />
          {isReady && success ? (
            <UI.Block gap="small" className="p-4 text-center">
              <p className="text-2xl font-semibold text-[#FF733A]">You&apos;re all set!</p>
              <p className="text-2xl text-selphWhite-500">Keep an eye on your inbox...</p>
            </UI.Block>
          ) : (
            <>
              <UI.Form.Form<SignupFormValue>
                initialValues={{ email: '' }}
                validationSchema={UI.Form.validation.schema({
                  email: { label: 'Email Address', type: 'string', required: true, criteria: 'emailAddress' },
                })}
                onSubmit={handleSubmit}
              >
                <UI.Block gap={{ default: 'small', md: 'large' }} className="p-4 text-center">
                  <UI.Block gap={{ default: 'xs', sm: 'small' }}>
                    <p className="text-3xl leading-none font-semibold text-selphWhite-500 sm:text-4xl md:text-5xl">
                      Want the Secrets to Better Health?
                    </p>

                    <p className="text-xl font-semibold text-[#FF733A]">
                      Our science-backed guide reveals 5 surprising truths.
                    </p>
                  </UI.Block>

                  <UI.Block gap="small" className="mx-auto w-[90%]">
                    <UI.Form.Text name="email" placeholder="Email address" theme="light" />
                    {isReady && !success && (
                      <p className="mt-1 text-sm text-selphRed-400">
                        Sorry, there was a problem signing you up. Please try again.
                      </p>
                    )}
                    <UI.Button type="submit" size="full">
                      <span className="text-2xl font-semibold sm:text-3xl">Get your Guide</span>
                    </UI.Button>
                  </UI.Block>
                  <UI.Paragraph size={{ default: 'xs', md: 'small' }} color={'white'}>
                    By signing up, you agree to receive Selph&apos;s emails on optimising your health. You can
                    unsubscribe at any time.
                  </UI.Paragraph>
                </UI.Block>
              </UI.Form.Form>
            </>
          )}
        </div>
      </UI.Modal>
    </>
  )
}

export default EmailOfferBanner
